"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { AlertCircle, CheckCircle2, Search, MessageSquare } from "lucide-react";
import combinedData from "../../data/Combined.json";
import { getAssertionAbbreviation, parseAssertions } from "../../utils/assertion-helpers";
import { ReviewComment } from "../shared/ReviewComment";

interface ImpairmentRommsProps { onBack?: () => void; }
interface RommItem { id: string; risk: string; assertion: string; options: string[]; }

export default function ImpairmentRomms({ onBack }: ImpairmentRommsProps) {
  const [romms, setRomms] = useState<RommItem[]>([]);
  const [selectedRommId, setSelectedRommId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [rommSelections, setRommSelections] = useState<Record<string, string>>({});
  const [rommDocumentation, setRommDocumentation] = useState<Record<string, string>>({});
  const [noteLines, setNoteLines] = useState<string[]>([]);
  
  // Review comment modal state
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);

  // Load ROMMs for "Impairment Testing" workspace (filtered by prefix in romm-id)
  useEffect(() => {
    const loadRomms = async () => {
      try {
        if (window.sharePointAPI?.readRommLibraryByWorkspace) {
          const res = await window.sharePointAPI.readRommLibraryByWorkspace("Impairment Testing");
          if (res.success && res.data?.romm_library) {
            // Data is already filtered by prefix in the IPC handler
            const data: RommItem[] = res.data.romm_library.map((e: any) => ({ id: e["romm-id"], risk: e.description, assertion: e.assertion, options: ["Lower", "Higher", "Significant", "NRPMM"] }));
            setRomms(data);
            if (data.length > 0) setSelectedRommId(data[0].id);

            const selections: Record<string, string> = {}; const docs: Record<string, string> = {};
            res.data.romm_library.forEach((e: any) => { if (e["assesment"]) selections[e["romm-id"]] = e["assesment"]; if (e["documentation"]) docs[e["romm-id"]] = e["documentation"]; });
            setRommSelections(selections); setRommDocumentation(docs);
            return;
          }
        }
      } catch {}
      setRomms([]); setSelectedRommId(null);
    };
    loadRomms();
  }, []);

  // Accounts/Areas from Combined.json: par === "Impairment Testing"; blank if none
  useEffect(() => {
    try {
      const entries = (combinedData as any).data.filter((x: any) => x.par === "Impairment Testing");
      const unique = [...new Set(entries.map((x: any) => x.note_line))].filter(Boolean);
      setNoteLines(unique as string[]);
    } catch {}
  }, []);

  const selectedRomm = romms.find((r) => r.id === selectedRommId);

  const handleRommSelection = async (rommId: string, option: string) => {
    setRommSelections((prev) => ({ ...prev, [rommId]: option }));
    try { if (window.sharePointAPI?.updateRommEntry) await window.sharePointAPI.updateRommEntry({ rommId, assessment: option, documentation: rommDocumentation[rommId] || "" }); } catch {}
  };

  const debouncedAutosave = useCallback(
    (() => { let t: NodeJS.Timeout; return (rommId: string, documentation: string) => { clearTimeout(t); t = setTimeout(async () => { try { if (window.sharePointAPI?.updateRommEntry) await window.sharePointAPI.updateRommEntry({ rommId, assessment: rommSelections[rommId] || "", documentation }); } catch {} }, 800); }; })(),
    [rommSelections]
  );

  const handleDocumentationChange = (rommId: string, documentation: string) => { setRommDocumentation((prev) => ({ ...prev, [rommId]: documentation })); debouncedAutosave(rommId, documentation); };

  const isRommComplete = (rommId: string) => rommSelections[rommId] !== undefined;
  const allRommsComplete = romms.every((r) => isRommComplete(r.id));

  const getRiskLevelColor = (option: string) => option === "Lower" ? "bg-yellow-300/20 text-yellow-300 border-yellow-300/30" : option === "Higher" ? "bg-yellow-700/30 text-yellow-400 border-yellow-700/30" : option === "Significant" ? "bg-red-500/20 text-red-400 border-red-500/30" : option === "NRPMM" ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-gray-500/20 text-gray-400 border-gray-500/30";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Risk of Material Misstatement (RoMMs) - Impairment Testing</h2>
          <p className="text-sm text-white/60">Assess the risk level for Impairment Testing assertions</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <Card className="border-white/10 bg-white/5 text-white lg:w-[30%] lg:flex-shrink-0">
          <CardHeader><CardTitle className="text-lg">Impairment Testing Risks</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input
                type="text"
                placeholder="Search ROMMs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border-white/20 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
              />
            </div>
            {/* Filtered ROMMs List */}
            {romms
              .filter((romm) => {
                const query = searchQuery.toLowerCase();
                return (
                  romm.id.toLowerCase().includes(query) ||
                  romm.risk.toLowerCase().includes(query) ||
                  romm.assertion.toLowerCase().includes(query)
                );
              })
              .map((romm) => (
              <div key={romm.id} className={`cursor-pointer rounded-lg border p-3 transition-all ${selectedRommId === romm.id ? "border-blue-500/50 bg-blue-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`} onClick={() => setSelectedRommId(romm.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{romm.id}</Badge>
                      {isRommComplete(romm.id) ? <CheckCircle2 className="h-4 w-4 text-green-400" /> : <AlertCircle className="h-4 w-4 text-yellow-400" />}
                    </div>
                    <p className="text-sm leading-relaxed text-white/80">{romm.risk}</p>
                  </div>
                </div>
              </div>
              ))}
            {romms.filter((romm) => {
              const query = searchQuery.toLowerCase();
              return (
                romm.id.toLowerCase().includes(query) ||
                romm.risk.toLowerCase().includes(query) ||
                romm.assertion.toLowerCase().includes(query)
              );
            }).length === 0 && searchQuery && (
              <div className="text-center py-6 text-white/60 text-sm">
                No ROMMs found matching "{searchQuery}"
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5 text-white lg:w-[70%] lg:flex-shrink-0">
          <CardHeader><CardTitle className="text-lg">{selectedRomm?.id || "Select a ROMM"}</CardTitle></CardHeader>
          <CardContent>
            <div className="mb-4 hidden rounded-lg border border-white/20 bg-white/10 p-3 md:grid md:grid-cols-4 md:gap-4">
              <div className="text-sm font-semibold text-white">Description</div>
              <div className="text-sm font-semibold text-white">Accounts/Areas</div>
              <div className="text-sm font-semibold text-white">Assertions/Type</div>
              <div className="text-sm font-semibold text-white">Assessment</div>
            </div>

            <div className="space-y-4">
              {selectedRomm && (
                <div className="rounded-lg border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10 md:grid md:grid-cols-4 md:gap-4 md:p-3">
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Description</div>
                    <div className="text-sm text-white/80">{selectedRomm.risk}</div>
                  </div>
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Accounts/Areas</div>
                    <div className="space-y-1">
                      {noteLines.length > 0 && noteLines.map((noteLine, index) => (
                        <div key={index} className="flex items-center gap-1 text-xs text-orange-400"><span className="h-2 w-2 rounded-full bg-orange-400"></span>{noteLine}</div>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Assertions/Type</div>
                    <div className="space-y-2">
                      {parseAssertions(selectedRomm.assertion).map((assertion, index) => (
                        <div key={index} className="flex items-center">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                            {getAssertionAbbreviation(assertion)}
                          </div>
                          <span className="ml-2 text-sm text-white">{assertion}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Assessment</div>
                    <div className="flex items-center gap-2">
                      <Select value={rommSelections[selectedRomm.id] || ""} onValueChange={(value) => handleRommSelection(selectedRomm.id, value)}>
                        <SelectTrigger className="w-full"><SelectValue placeholder="Select assessment" /></SelectTrigger>
                        <SelectContent>
                          {selectedRomm.options.map((option) => (
                            <SelectItem key={option} value={option}><div className="flex items=center gap-2"><Badge className={`text-xs ${getRiskLevelColor(option)}`}>{option}</Badge></div></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-md font-medium text-white">Risk Assessment Documentation</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReviewCommentOpen(true)}
                      className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
                      aria-label="Review Comments"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button onClick={async () => { if (!selectedRommId) return; try { if (window.sharePointAPI?.updateRommEntry) { await window.sharePointAPI.updateRommEntry({ rommId: selectedRommId, assessment: rommSelections[selectedRommId] || "", documentation: rommDocumentation[selectedRommId] || "", controlIds: [], procedureIds: [], }); } } catch {} }} className="bg-green-600 text-white hover:bg-green-700" disabled={!rommSelections[selectedRomm.id]}>Submit to SharePoint</Button>
                </div>
                <Textarea placeholder="Enter your risk assessment documentation here..." value={rommDocumentation[selectedRomm.id] || ""} onChange={(e) => handleDocumentationChange(selectedRomm.id, e.target.value)} className="min-h-[120px] bg-white/5 border-white/20 text-white placeholder:text-white/50 focus:border-blue-500/50 focus:ring-blue-500/20" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-4"><div className="text-sm text-white/60">Complete all risk assessments to proceed to the next step</div><Button disabled={!allRommsComplete} className="bg-blue-600 text-white hover:bg-blue-700">Complete Risk Assessment</Button></div>

      {/* Review Comment Modal */}
      {selectedRommId && (
        <ReviewComment
          questionId={selectedRommId}
          questionText={`Risk Assessment Documentation - ${selectedRommId}`}
          isOpen={reviewCommentOpen}
          onClose={() => setReviewCommentOpen(false)}
        />
      )}
    </div>
  );
}

